/**
 * 积分状态管理 Store
 * 从 CsdPublic SDK 获取用户积分信息
 */

import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getUserCredits } from '@/api/auth';

/**
 * 积分状态管理 Store
 */
export const useCreditsStore = defineStore('credits', () => {
  /** 当前积分余额（-1 表示未获取或异常） */
  const balance = ref<number>(-1);

  /** 加载状态 */
  const loading = ref(false);

  /** 上次获取时间戳 */
  const lastFetchedAt = ref<number>(0);

  /** 数据来源（csd 或 backend） */
  const source = ref<string>('');

  /** 是否是初始加载（用于区分首次加载失败和后续刷新失败） */
  const isInitialized = ref(false);

  /** 上次有效的积分值（用于后台刷新失败时保持显示） */
  const lastValidBalance = ref<number>(-1);

  /**
   * 获取积分余额
   * 直接从后端 API 获取用户积分（业务积分存在后端 users 表）
   */
  async function fetchBalance() {
    if (loading.value) return;
    loading.value = true;

    try {
      const data = await getUserCredits();
      balance.value = data.credits ?? 0;
      lastValidBalance.value = balance.value;
      source.value = 'backend';
      lastFetchedAt.value = Date.now();
      isInitialized.value = true;
    } catch (error) {
      console.error('[积分Store] 获取余额失败', error);
      if (!isInitialized.value) {
        balance.value = -1;
      } else {
        balance.value = lastValidBalance.value;
      }
    } finally {
      loading.value = false;
    }
  }

  /**
   * 刷新余额（模型调用完成后调用）
   */
  async function refreshBalance() {
    await fetchBalance();
  }

  return {
    balance,
    loading,
    lastFetchedAt,
    source,
    isInitialized,
    lastValidBalance,
    fetchBalance,
    refreshBalance,
  };
});
